/**
 * MVP delivery board — display constants and grouping config.
 *
 * The board manages every FEATURE of the YangoDeli B2B MVP (migrations
 * 0105–0107). Reference dimensions (workstreams, milestones, components) live in
 * the DB; this file holds only the presentation enums and the group-by config.
 */

import type {
  BoardImplKind,
  DeliveryStatus,
  MvpImplState,
  MvpStage,
} from "@/infrastructure/supabase/database.types";

/**
 * The StatusDot tones the delivery statuses use. This is the SINGLE SOURCE OF
 * TRUTH for delivery-status colour: the board dots read it directly, and the
 * timeline lane fill derives its raw CSS colours from it (DELIVERY_STATUS_COLOR
 * below), so the two surfaces can never drift apart.
 *
 * Palette (agreed): done = green, in-review = yellow, in-progress = purple,
 * blocked = red, not-started / deferred = muted.
 */
type DeliveryTone = "open" | "resolved" | "blocked" | "amber" | "purple";

export const DELIVERY_STATUS_TONE: Record<DeliveryStatus, DeliveryTone> = {
  "not-started": "open",
  "in-progress": "purple",
  blocked: "blocked",
  "in-review": "amber",
  done: "resolved",
  deferred: "open",
};

/** The raw CSS colour each StatusDot tone paints — mirrors status-dot.tsx's cva. */
const TONE_CSS: Record<DeliveryTone, string> = {
  open: "var(--muted-foreground)",
  resolved: "var(--rag-green)",
  blocked: "var(--rag-red)",
  amber: "var(--rag-amber)",
  purple: "var(--rag-purple)",
};

/**
 * Maps a delivery status onto a raw CSS colour, for surfaces that FILL AN AREA
 * rather than draw a StatusDot — currently the timeline lane bars, whose segmented
 * fill shows the status mix of a workstream's board features.
 *
 * DERIVED from DELIVERY_STATUS_TONE via TONE_CSS, deliberately: the timeline fill
 * and the board dot for a given status are the same colour BY CONSTRUCTION, so a
 * future recolour touches one map and both surfaces follow.
 */
export const DELIVERY_STATUS_COLOR: Record<DeliveryStatus, string> = Object.fromEntries(
  (Object.keys(DELIVERY_STATUS_TONE) as DeliveryStatus[]).map((status) => [
    status,
    TONE_CSS[DELIVERY_STATUS_TONE[status]],
  ])
) as Record<DeliveryStatus, string>;

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  "not-started": "Not started",
  "in-progress": "In progress",
  blocked: "Blocked",
  "in-review": "In review",
  done: "Done",
  deferred: "Deferred",
};

/** Display + workflow order for the delivery statuses (kanban column order). */
export const DELIVERY_STATUS_ORDER: DeliveryStatus[] = [
  "not-started",
  "in-progress",
  "in-review",
  "blocked",
  "done",
  "deferred",
];

/** Statuses that count as "shipped" for progress math. */
export function isDoneStatus(status: DeliveryStatus): boolean {
  return status === "done";
}

export const STAGE_LABEL: Record<MvpStage, string> = {
  "source-enrich": "Source & Enrich",
  "procure-stock": "Procure & Stock",
  "price-sell": "Price & Sell",
  "fulfil-deliver": "Fulfil & Deliver",
  "bill-serve": "Bill & Serve",
  "cross-cutting": "Cross-cutting",
};

export const STAGE_ORDER: MvpStage[] = [
  "source-enrich",
  "procure-stock",
  "price-sell",
  "fulfil-deliver",
  "bill-serve",
  "cross-cutting",
];

/**
 * Monday-style board table columns (after the sticky Item column). `grid`
 * drives both the header and every row so cells stay aligned; the Item column
 * is the leading `minmax(280px, 2fr)` track. `minWidth` floors the scrollable
 * width so the board scrolls horizontally on small screens with Item pinned.
 */
export const BOARD_COLUMNS: { key: string; label: string; width: string }[] = [
  // Block sits first: it's the board's primary grouping axis, and editing it
  // moves the item between groups, so it needs to be reachable without
  // horizontal scrolling.
  { key: "block", label: "Block", width: "190px" },
  { key: "status", label: "Status", width: "156px" },
  { key: "pr", label: "PR", width: "84px" },
  { key: "owner", label: "Owner", width: "150px" },
  { key: "milestone", label: "Milestone", width: "120px" },
  { key: "due", label: "Due", width: "110px" },
  { key: "component", label: "Component", width: "190px" },
  { key: "impl", label: "Impl", width: "140px" },
  { key: "domain", label: "Domain", width: "160px" },
  { key: "deps", label: "Depends on", width: "96px" },
  { key: "critical", label: "Critical", width: "84px" },
];

export const BOARD_MIN_WIDTH = 2004;

export type BoardColumn = (typeof BOARD_COLUMNS)[number];

/** Column sort. `key` is a column key (or "item" for the pinned title column). */
export type SortDir = "asc" | "desc";
export interface BoardSort {
  key: string;
  dir: SortDir;
}

/**
 * Columns that can be sorted. "item" is the pinned first column (sorts by code);
 * "deps" is excluded (a relationship cell, not a comparable scalar).
 */
export const SORTABLE_COLUMN_KEYS = new Set([
  "item",
  "block",
  "status",
  "pr",
  "owner",
  "milestone",
  "due",
  "component",
  "impl",
  "domain",
  "critical",
]);

/** Cycle a column's sort on header click: none → asc → desc → none. */
export function nextSort(current: BoardSort | null, key: string): BoardSort | null {
  if (!current || current.key !== key) return { key, dir: "asc" };
  if (current.dir === "asc") return { key, dir: "desc" };
  return null;
}

/** Default left-to-right order of the reorderable columns (Item is always pinned first). */
export const DEFAULT_COLUMN_ORDER = BOARD_COLUMNS.map((c) => c.key);

export const COLUMN_ORDER_STORAGE_KEY = "mvp-board-column-order";

/** Group ids the user has collapsed, persisted so the board reopens as they left it. */
export const COLLAPSED_GROUPS_STORAGE_KEY = "mvp-board-collapsed-groups";

/** Resolve a stored key order into column defs, tolerating unknown/missing keys. */
export function resolveColumns(order: string[]): BoardColumn[] {
  const byKey = new Map(BOARD_COLUMNS.map((c) => [c.key, c]));
  const ordered: BoardColumn[] = [];
  for (const key of order) {
    const col = byKey.get(key);
    if (col) ordered.push(col);
  }
  // Splice in columns missing from the stored order (newly added ones) at their
  // DEFAULT index rather than appending. Anyone with a saved order predates the
  // new column, and appending would bury it off the right edge of the scroll
  // area — which for a column like Block, whose whole point is being reachable,
  // means the feature effectively doesn't exist for existing users.
  BOARD_COLUMNS.forEach((col, defaultIndex) => {
    if (!order.includes(col.key)) ordered.splice(defaultIndex, 0, col);
  });
  return ordered;
}

/** Grid template for the table: pinned Item column + the ordered reorderable columns. */
export function buildGridTemplate(columns: BoardColumn[]): string {
  return `minmax(520px, 3fr) ${columns.map((c) => c.width).join(" ")}`;
}

export const IMPL_KIND_LABEL: Record<BoardImplKind, string> = {
  native: "Native config",
  build: "Build-on-top",
  scratch: "From scratch",
  mixed: "Mixed",
};

export const IMPL_STATE_LABEL: Record<MvpImplState, string> = {
  build: "Build",
  integrate: "Integrate",
  kept: "Kept",
  future: "Future",
};

/** How the board can be grouped. Workstream (the build sequence) is the default. */
export type BoardGroupBy = "workstream" | "component" | "milestone" | "stage" | "status";

export const GROUP_BY_OPTIONS: { value: BoardGroupBy; label: string }[] = [
  { value: "workstream", label: "Workstream" },
  { value: "component", label: "Component" },
  { value: "milestone", label: "Milestone" },
  { value: "stage", label: "Stage" },
  { value: "status", label: "Status" },
];
